/**
 * Road Detection Hook (feature-agnostic)
 *
 * Shared core for the country-aware `RoadDisplayField` GPS + manual-input flow.
 * Given an anchor point (lat/lng) and a GPS suggestion source, it detects the
 * road on anchor change, applies the resulting GPS suggestion to the location
 * field, and exposes manual-input + manual-GPS-request handlers.
 *
 * Feature-specific wrappers (`useNoteRoadDetection`, `useObstructionRoadDetection`)
 * read their slice's anchor point and delegate here so the detection logic lives
 * in exactly one place.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRoadMapping } from '@/hooks/useRoadMapping';
import type { useGPSLocationSuggestion } from '@/hooks/useGPSLocationSuggestion';
import type { RoadSource } from '@/components/common/RoadDisplayField';

interface UseRoadDetectionProps {
  anchorLat: number | null;
  anchorLng: number | null;
  gpsLocationSuggestion: ReturnType<typeof useGPSLocationSuggestion>;
  setLocation: (value: string) => void;
}

interface UseRoadDetectionReturn {
  roadSource: RoadSource;
  handleRequestGPSDetection: () => void;
  handleManualRoadInput: (value: string) => void;
}

export function useRoadDetection({
  anchorLat,
  anchorLng,
  gpsLocationSuggestion,
  setLocation,
}: UseRoadDetectionProps): UseRoadDetectionReturn {
  const { t } = useTranslation();
  const { mapGPSToRoad } = useRoadMapping(t);

  // Destructure stable members to avoid new-object-reference deps.
  const { detectLocation, suggestion, clearSuggestion } = gpsLocationSuggestion;

  const [roadSource, setRoadSource] = useState<RoadSource>(null);
  const isDetectionInProgressRef = useRef(false);
  const needsRedetectionRef = useRef(false);
  const [redetectKey, setRedetectKey] = useState(0);

  // Detect road when anchor point changes; re-detect when a detection is queued.
  useEffect(() => {
    if (anchorLat === null || anchorLng === null) return;

    if (isDetectionInProgressRef.current) {
      needsRedetectionRef.current = true;
      return;
    }

    isDetectionInProgressRef.current = true;
    needsRedetectionRef.current = false;
    detectLocation({ lat: anchorLat, lng: anchorLng }).finally(() => {
      isDetectionInProgressRef.current = false;
      if (needsRedetectionRef.current) {
        needsRedetectionRef.current = false;
        setRedetectKey((key) => key + 1);
      }
    });
  }, [anchorLat, anchorLng, detectLocation, redetectKey]);

  // Apply GPS suggestion when it arrives.
  useEffect(() => {
    if (!suggestion) return;

    const mappedRoad = mapGPSToRoad(suggestion.suggestion);
    if (mappedRoad) {
      setLocation(mappedRoad);
      setRoadSource('gps');
    }

    clearSuggestion();
  }, [suggestion, mapGPSToRoad, setLocation, clearSuggestion]);

  const handleManualRoadInput = useCallback(
    (value: string) => {
      setRoadSource(value ? 'manual' : null);
      setLocation(value);
    },
    [setLocation]
  );

  const handleRequestGPSDetection = useCallback(() => {
    if (anchorLat === null || anchorLng === null) return;

    isDetectionInProgressRef.current = true;
    detectLocation({ lat: anchorLat, lng: anchorLng }).finally(() => {
      isDetectionInProgressRef.current = false;
    });
  }, [anchorLat, anchorLng, detectLocation]);

  return {
    roadSource,
    handleRequestGPSDetection,
    handleManualRoadInput,
  };
}
